<template>
    <v-dialog
        :value="reserveUnitModal"
        fullscreen
        hide-overlay
        persistent
        transition="dialog-bottom-transition"
        :content-class="twilioChat ? 'twilioDrawer' : ''"
    >
        <v-card>
            <v-card-title class="text-h5" primary-title>
                <v-layout>
                    <v-flex>New Lead/Reservation</v-flex>
                </v-layout>
            </v-card-title>
            <v-divider />
            <v-card-text>
                <v-stepper v-model="currentStep">
                    <v-stepper-header>
                        <v-stepper-step
                            :color="currentStep === 1 ? 'secondary' : 'success'"
                            :complete="currentStep > 1"
                            step="1"
                            :class="currentStep === 1 ? 'title' : 'subheading'"
                        >
                            Reservation Options
                        </v-stepper-step>
                        <v-divider :class="currentStep > 1 ? 'success' : null" />
                        <v-stepper-step
                            :color="currentStep <= 2 ? 'secondary' : 'success'"
                            :complete="currentStep > 2"
                            step="2"
                            :class="currentStep === 2 ? 'title' : 'subheading'"
                        >
                            Reservation Review
                        </v-stepper-step>
                        <v-divider :class="currentStep > 2 ? 'success' : null" />
                        <v-stepper-step
                            color="success"
                            :complete="currentStep === 3"
                            step="3"
                            :class="currentStep === 3 ? 'title' : 'subheading'"
                        >
                            Reservation Success
                        </v-stepper-step>
                    </v-stepper-header>
                    <v-stepper-items>
                        <v-stepper-content step="1">
                            <reservation-options :tenant="tenant" />
                        </v-stepper-content>
                        <v-stepper-content step="2">
                            <reservation-review
                                :currentStep="currentStep"
                                :reservedUnit="reservedUnit"
                                @toOptions="toOptions"
                            />
                        </v-stepper-content>
                        <v-stepper-content step="3">
                            <reservation-success
                                :currentStep="currentStep"
                                :reservedUnit="reservedUnit"
                                :waitingListId="waitingListId"
                            />
                        </v-stepper-content>
                    </v-stepper-items>
                </v-stepper>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-layout v-if="currentStep === 3">
                    <v-btn
                        data-testid="reservation-dismiss-btn"
                        class="reservation-dismiss-btn mt-2 ml-3"
                        color="error"
                        @click="reservationSuccess"
                        large
                    >
                        Dismiss
                    </v-btn>
                    <v-spacer />
                    <disposition
                      :dispositionOpen="dispositionOpen"
                      @collapse="dispositionOpen = false"
                    >
                        <v-btn
                            data-testid="reservation-disposition-btn"
                            class="reservation-disposition-btn mt-2 mr-3"
                            color="primary"
                            @click.stop="dispositionOpen = true"
                            large
                        >
                            Disposition
                        </v-btn>
                    </disposition>
                </v-layout>

                <v-layout v-else>
                    <v-btn
                        data-testid="reservation-close-btn"
                        class="reservation-close-btn error mt-2 ml-3"
                        @click="collapse"
                        large
                    >
                        Close
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        data-testid="reservation-to-summary-btn"
                        class="reservation-to-summary-btn primary mt-2 mr-3"
                        v-if="currentStep === 1"
                        :disabled="!canSubmitReservation"
                        @click="toSummary"
                        large
                    >
                        View Summary
                    </v-btn>
                    <v-btn
                        data-testid="reservation-create-reservation-btn"
                        class="reservation-create-reservation-btn success mt-2 mr-3"
                        v-if="currentStep === 2"
                        :loading="reserveUnitLoading"
                        @click="createReservation"
                        large
                    >
                        Submit
                    </v-btn>
                </v-layout>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import Disposition from "@/components/Disposition";
import ReservationOptions from "@/components/reserve-unit/steps/ReservationOptions";
import ReservationReview from "@/components/reserve-unit/steps/ReservationReview";
import ReservationSuccess from "@/components/reserve-unit/steps/ReservationSuccess";
import { mapState } from "vuex";
import moment from "moment";

export default {
  props: ["reservedUnit", "reserveUnitModal", "tenant"],

  data() {
    return {
      reserveUnitLoading: false,
      paymentLoading: false,

      visible: false,
      dispositionOpen: false,

      tenant_id: null,
      waitingListId: "",
      note: "",

      currentStep: 1
    };
  },

  methods: {
    toOptions() {
      this.currentStep = 1;
    },

    toSummary() {
      this.currentStep = 2;
    },

    toConfirmation() {
      this.currentStep = 3;
    },

    collapse() {
      this.$emit("collapse");
      this.toOptions();
    },

    reservationSuccess() {
      this.$emit("reservationCreated");
      this.collapse();
    },

    createReservation() {
      this.reserveUnitLoading = true;
      this.$store.dispatch("createReservation", this.reserveUnitPayload)
        .then(resp => {
          this.waitingListId = resp.data.waiting_list_id;

          this.$store.commit("setReservationData", this.reserveUnitPayload);
          this.$store.commit("setReservationId", resp.data.id);

          this.$store.dispatch("handleSuccess", "Reservation successfully created!");

          this.toConfirmation();
          this.reserveUnitLoading = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.reserveUnitLoading = false;
        });
    }

  },

  computed: {
    ...mapState({
      info: state => state.tenant.info,
      options: state => state.tenant.options,
      darkMode: state => state.application.settings.darkTheme,
      twilioChat: state => state.application.twilioChat
    }),

    canSubmitReservation() {
      let regex = /^\S+@\S+\.\S+$/;
      return (
        this.info.first_name &&
                this.info.last_name &&
                regex.test(this.info.email) &&
                (this.info.phone_number && this.info.phone_number.length === 12) &&
                this.options.reservationType &&
                this.options.moveInDate &&
                this.options.followupDate
      );
    },

    reserveUnitPayload() {
      let externalUnitIdAry = this.reservedUnit.unit_id_first_available.split(",");
      let externalUnitId = externalUnitIdAry.length > 0 ? externalUnitIdAry[0] : null;
      let externalPromotionId = parseInt(this.reservedUnit.concession_id) === 0 ? null : `${this.reservedUnit.concession_id}`;
      let payload = {
        first_name: this.info.first_name,
        last_name: this.info.last_name,
        email: this.info.email,
        phone: this.info.phone_number,
        customer_source: localStorage.getItem("customerSource"),
        move_in_date: this.options.moveInDate,
        type: this.options.reservationType,
        followup_date: moment(this.options.followupDate).format(),
        quoted_price: this.reservedUnit.rate,
        external_unit_id: externalUnitId,
        external_unit_type_id: this.reservedUnit.type_id,
        unit_dimensions: this.reservedUnit.width + " x " + this.reservedUnit.length,
        external_promotion_id: externalPromotionId,
        facility_id: this.$route.params.facilityId
      };

      if (this.info.external_id) {
        payload.external_tenant_id = `${this.info.external_id}`;
      }
      return payload;
    }
  },

  components: {
    Disposition,
    ReservationOptions,
    ReservationReview,
    ReservationSuccess
  }
};
</script>
